import { useCallback, useMemo, useRef, useState } from "react";
import { Box, GlobalStyles, Paper, Typography } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import type { ColDef, GridApi, ICellRendererParams } from "ag-grid-community";
import { ErrorModal } from "@dexteel/mesf-core";
import moment, { type Moment } from "moment";

import { CenteredLazyLoading } from "@/controls/LazyLoading";
import {
	isUsableDate,
	urlParam,
	useUrlState,
} from "@/shared/hooks/useUrlState";
import { formatProxyDate } from "@/shared/utils/proxyDateTime";
import { useReturnedRolls } from "../hooks/useReturnedRolls";
import type { ReturnedRollRow } from "../models/ReturnedRolls";
import { ReturnedRollsSummary } from "./ReturnedRollsSummary";
import { ReturnedRollsToolbar } from "./ReturnedRollsToolbar";
import { STATUS_BY_VALUE, StatusSquare } from "./statusMeta";

const CENTER_CELL = { textAlign: "center" } as const;
const FLEX_CENTER_CELL = {
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
} as const;
const CENTER_HEADER = "ag-cell-center-header";

const toFixedOrEmpty = (value: unknown, decimals: number): string =>
	value == null ? "" : Number(value).toFixed(decimals);

// One badge per roll: its single status (Returned / Scrapped / Broken).
const StatusBadge = (p: ICellRendererParams<ReturnedRollRow>) => {
	const meta = p.value ? STATUS_BY_VALUE[p.value as string] : undefined;
	if (!meta) return null;
	return (
		<StatusSquare letter={meta.letter} color={meta.color} title={meta.label} />
	);
};

const buildColumnDefs = (): ColDef<ReturnedRollRow>[] => [
	{
		field: "Name",
		headerName: "Foundry Number",
		width: 150,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "Status",
		headerName: "Status",
		width: 64,
		sortable: true,
		cellStyle: { ...FLEX_CENTER_CELL, padding: 0 },
		headerClass: CENTER_HEADER,
		cellRenderer: StatusBadge,
	},
	{
		field: "Diameter",
		headerName: "Diameter",
		width: 120,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => toFixedOrEmpty(p.value, 3),
	},
	{
		field: "ScrapDiameter",
		headerName: "Scrap Diameter",
		width: 140,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => toFixedOrEmpty(p.value, 3),
	},
	{
		field: "Hardness",
		headerName: "Hardness",
		width: 110,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "MillSize",
		headerName: "Mill Size",
		width: 130,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "RollMaterial",
		headerName: "Roll Material",
		flex: 1,
		minWidth: 160,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "Cost",
		headerName: "Cost",
		width: 120,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => toFixedOrEmpty(p.value, 3),
	},
	{
		field: "ReceivedString",
		headerName: "Received",
		width: 170,
		sortable: true,
		sort: "asc",
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => formatProxyDate(p.value),
	},
	{
		field: "ReturnedString",
		headerName: "Returned",
		width: 170,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => formatProxyDate(p.value),
	},
];

// Legacy filter: 3 checkboxes, all checked by default. The legacy URL called them
// scrap, broken and returned.
const URL_SCHEMA = {
	start: urlParam.date(() => moment().startOf("day")),
	end: urlParam.date(() => moment().startOf("day")),
	scrap: urlParam.bool(true),
	broken: urlParam.bool(true),
	returned: urlParam.bool(true),
};

export const ReturnedRollsReport = () => {
	const [{ start, end, scrap: scrapped, broken, returned }, setFilters] =
		useUrlState(URL_SCHEMA);
	const [errorMsg, setErrorMsg] = useState("");
	const gridApiRef = useRef<GridApi<ReturnedRollRow> | null>(null);

	const columnDefs = useMemo(() => buildColumnDefs(), []);

	const startStr = start.format("YYYY-MM-DD");
	const endStr = end.format("YYYY-MM-DD");

	const dataQuery = useReturnedRolls({
		start: startStr,
		end: endStr,
	});

	// Client-side filter by the displayed status — each checkbox toggles its own
	// status independently (unlike the legacy SP, where "Returned" was a dataset
	// mode switch).
	const allRows = dataQuery.data ?? [];
	const rows = useMemo(
		() =>
			allRows.filter((r) => {
				if (r.Status === "Returned") return returned;
				if (r.Status === "Scrapped") return scrapped;
				if (r.Status === "Broken") return broken;
				return true;
			}),
		[allRows, returned, scrapped, broken],
	);

	const handleExportToExcel = useCallback(() => {
		if (!gridApiRef.current) return;
		gridApiRef.current.exportDataAsExcel({
			fileName: `Returned_Rolls_${startStr}_${endStr}.xlsx`,
			processCellCallback: (params) => {
				const colId = params.column.getColId();
				if (colId === "ReceivedString" || colId === "ReturnedString") {
					return formatProxyDate(params.value);
				}
				if (
					colId === "Diameter" ||
					colId === "ScrapDiameter" ||
					colId === "Cost"
				) {
					return toFixedOrEmpty(params.value, 3);
				}
				return params.value ?? "";
			},
		});
	}, [startStr, endStr]);

	if (dataQuery.error && !errorMsg) {
		setErrorMsg((dataQuery.error as Error).message);
	}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "calc(100vh - 130px)",
				gap: 1,
				p: 1,
				bgcolor: "#f0f2f5",
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600 }}>
				Returned Rolls
			</Typography>
			<GlobalStyles
				styles={{
					".ag-cell-center-header .ag-header-cell-label": {
						justifyContent: "center",
					},
				}}
			/>
			<Paper elevation={3}>
				<ReturnedRollsToolbar
					start={start}
					end={end}
					scrapped={scrapped}
					broken={broken}
					returned={returned}
					onStartChange={(d) => isUsableDate(d) && setFilters({ start: d })}
					onEndChange={(d) => isUsableDate(d) && setFilters({ end: d })}
					onScrappedChange={(value) => setFilters({ scrap: value })}
					onBrokenChange={(value) => setFilters({ broken: value })}
					onReturnedChange={(value) => setFilters({ returned: value })}
					onExportExcel={handleExportToExcel}
				/>
			</Paper>

			<ReturnedRollsSummary total={rows.length} />

			<Paper
				elevation={3}
				sx={{
					flex: 1,
					overflow: "hidden",
				}}
			>
				{dataQuery.isLoading ? (
					<CenteredLazyLoading />
				) : (
					<AgGridReact<ReturnedRollRow>
						columnDefs={columnDefs}
						rowData={rows}
						rowModelType="clientSide"
						defaultColDef={{ resizable: true }}
						suppressContextMenu
						headerHeight={32}
						rowHeight={28}
						onGridReady={(e) => {
							gridApiRef.current = e.api;
						}}
					/>
				)}
			</Paper>

			<ErrorModal error={errorMsg} onHide={() => setErrorMsg("")} />
		</Box>
	);
};
